/**
 * View-model hook for the transcript's timeline section.
 *
 * Encapsulates the dataflow from raw events + configuration to everything the
 * swimlane UI and the event-node tree need: event-type filtering, branch
 * detection, config resolution, the timeline pipeline itself, the swimlane
 * visibility policy, and the choice of which events feed the node tree.
 */

import { useMemo } from "react";

import type {
  Event,
  Timeline as ServerTimeline,
} from "@tsmono/inspect-common/types";

import { spanHasBranches, type TimelineSpan } from "../timeline/core";
import {
  useTimelineConfig,
  useTimelinesArray,
  useTranscriptTimeline,
  type TimelineOptions,
  type TranscriptTimelineResult,
  type UseActiveTimelineProps,
  type UseTimelineConfigResult,
  type UseTimelineProps,
} from "../timeline/hooks";
import { type MarkerConfig } from "../timeline/markers";

// =============================================================================
// Types
// =============================================================================

/** The events (and optional agent spans) that feed the EventNode tree. */
export interface EventNodeFeed {
  /** Events for the node tree: scoped to the swimlane selection when swimlanes are shown, with hidden types removed. */
  events: Event[];
  /** Agent spans for card rendering; undefined when swimlanes are hidden. */
  sourceSpans: ReadonlyMap<string, TimelineSpan> | undefined;
}

export interface UseTimelinePipelineOptions {
  /** The flat event array for the sample. */
  events: Event[];
  /** Event types to hide from the rendered card list. Applied after timeline
   *  construction so structural events (anchor/branch) still resolve. */
  hiddenEventTypes?: readonly string[];
  /** Server-provided timelines (used when available instead of building from events). */
  serverTimelines?: ServerTimeline[];
  /** Override the user's marker config preference. */
  markerConfig?: MarkerConfig;
  /** Override the user's agent/timeline options preference. */
  agentConfig?: TimelineOptions;
  /** Swimlane visibility: explicit, or "auto" from timeline structure. */
  showSwimlanes?: boolean | "auto";
  /** Props for timeline selection state. */
  timelineSelection?: UseTimelineProps;
  /** Props for active timeline state. */
  activeTimeline?: UseActiveTimelineProps;
}

export interface TimelinePipelineResult {
  /** Full timeline pipeline result (selection state, layouts, navigation). */
  timeline: TranscriptTimelineResult;
  /** Resolved timeline config (persistent user preferences). */
  timelineConfig: UseTimelineConfigResult;
  /** Whether the swimlane section should render. */
  showSwimlanes: boolean;
  /** Default collapsed state for the swimlane section (undefined = component default). */
  swimlanesDefaultCollapsed: boolean | undefined;
  /** Events + spans that feed the EventNode tree. */
  nodeFeed: EventNodeFeed;
  /** Events eligible for transcript search (hidden types removed, unscoped by selection). */
  searchableEvents: Event[];
}

// =============================================================================
// Hook
// =============================================================================

export function useTimelinePipeline(
  options: UseTimelinePipelineOptions
): TimelinePipelineResult {
  const {
    events,
    hiddenEventTypes,
    serverTimelines,
    markerConfig: markerConfigOverride,
    agentConfig: agentConfigOverride,
    showSwimlanes: showSwimlanesOption = "auto",
    timelineSelection,
    activeTimeline,
  } = options;

  // Apply the user's event-type filter to the timeline pipeline so hidden
  // types don't leave behind empty swimlane rows (e.g. an "init" span whose
  // only content was a filtered `sample_init`). `anchor` events are always
  // preserved — they're structural references used by `convertServerTimeline`
  // to position fork navigators, not display content.
  const eventsForTimeline = useMemo(() => {
    if (!hiddenEventTypes || hiddenEventTypes.length === 0) return events;
    return events.filter(
      (e) => e.event === "anchor" || !hiddenEventTypes.includes(e.event)
    );
  }, [events, hiddenEventTypes]);

  // Detect whether any timeline in this sample contains branches so the
  // config hook can default `showBranches` on (when the user has not
  // explicitly toggled it). `useTimelinesArray` is memoized, so the redundant
  // call inside `useTranscriptTimeline` below reuses the same result.
  const timelinesForBranchDetection = useTimelinesArray(
    eventsForTimeline,
    serverTimelines
  );
  const branchesPresent = useMemo(
    () => timelinesForBranchDetection.some((tl) => spanHasBranches(tl.root)),
    [timelinesForBranchDetection]
  );

  const timelineConfig = useTimelineConfig({ branchesPresent });
  const resolvedMarkerConfig =
    markerConfigOverride ?? timelineConfig.markerConfig;
  const resolvedAgentConfig = agentConfigOverride ?? timelineConfig.agentConfig;

  const timeline = useTranscriptTimeline({
    events: eventsForTimeline,
    markerConfig: resolvedMarkerConfig,
    timelineOptions: resolvedAgentConfig,
    serverTimelines,
    timelineProps: timelineSelection,
    activeTimelineProps: activeTimeline,
  });

  const { hasTimeline, hasAgentTimeline } = timeline;
  const { regionCounts } = timeline.swimlanes;
  const { timelines } = timeline.multiTimeline;
  const { events: selectedEvents, sourceSpans } = timeline.selection;

  const showSwimlanes = useMemo(() => {
    if (showSwimlanesOption === "auto") {
      return (
        hasTimeline ||
        regionCounts.size > 0 ||
        timelines.length > 1 ||
        timeline.views.stack.length > 0
      );
    }
    return showSwimlanesOption;
  }, [
    showSwimlanesOption,
    hasTimeline,
    regionCounts,
    timelines.length,
    timeline.views.stack.length,
  ]);

  const swimlanesDefaultCollapsed = useMemo(() => {
    if (
      showSwimlanesOption === "auto" &&
      !hasTimeline &&
      regionCounts.size === 0
    ) {
      return true;
    }
    if (hasTimeline) {
      // Expand by default only when there's agent sub-structure to drill into.
      // A bare main + scoring (or init) timeline has nothing to expand, so
      // default it collapsed.
      return hasAgentTimeline ? false : true;
    }
    return undefined;
  }, [showSwimlanesOption, hasTimeline, hasAgentTimeline, regionCounts]);

  // With swimlanes on, the node tree shows only the selected row's events;
  // otherwise it shows the full (unfiltered-by-selection) event stream.
  const rawEventsForNodes = showSwimlanes ? selectedEvents : events;
  const eventsForNodes = useMemo(
    () =>
      hiddenEventTypes && hiddenEventTypes.length > 0
        ? rawEventsForNodes.filter((e) => !hiddenEventTypes.includes(e.event))
        : rawEventsForNodes,
    [rawEventsForNodes, hiddenEventTypes]
  );

  const nodeFeed = useMemo<EventNodeFeed>(
    () => ({
      events: eventsForNodes,
      sourceSpans: showSwimlanes ? sourceSpans : undefined,
    }),
    [eventsForNodes, showSwimlanes, sourceSpans]
  );

  // Honor the same event-type filter the renderer uses. State/store events
  // carry huge JSON payloads but aren't surfaced in the transcript tree;
  // matching their fields would inflate the counter with unreachable results.
  const searchableEvents = useMemo(() => {
    if (!hiddenEventTypes || hiddenEventTypes.length === 0) return events;
    const hidden = new Set(hiddenEventTypes);
    return events.filter((e) => !hidden.has(e.event));
  }, [events, hiddenEventTypes]);

  return {
    timeline,
    timelineConfig,
    showSwimlanes,
    swimlanesDefaultCollapsed,
    nodeFeed,
    searchableEvents,
  };
}
